/**
 * Dummy Data for Frontend Development
 * 
 * This file contains all dummy data used when the real API is not available.
 * Data structures match the API response formats defined in frontend-requirements.md
 * 
 * When switching to real API, this file can be removed or kept for testing.
 */

// ============================================
// REGIONS
// ============================================
export const DUMMY_REGIONS = [
  { region_code: 'GLOBAL', region_name: 'Overall', region_scope: 'global' },
  { region_code: 'US', region_name: 'United States', region_scope: 'country' },
  { region_code: 'JP', region_name: 'Japan', region_scope: 'country' },
  { region_code: 'GB', region_name: 'United Kingdom', region_scope: 'country' },
  { region_code: 'DE', region_name: 'Germany', region_scope: 'country' },
  { region_code: 'FR', region_name: 'France', region_scope: 'country' },
  { region_code: 'KR', region_name: 'South Korea', region_scope: 'country' },
  { region_code: 'BR', region_name: 'Brazil', region_scope: 'country' }
];

// ============================================
// PLATFORMS
// ============================================
export const DUMMY_PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: '📺', color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', color: '#000000' },
  { id: 'reddit', name: 'Reddit', icon: '🔴', color: '#FF4500' },
  { id: 'instagram', name: 'Instagram', icon: '📷', color: '#E1306C' },
  { id: 'twitter', name: 'Twitter/X', icon: '𝕏', color: '#1DA1F2' },
  { id: 'youtube_music', name: 'YouTube Music', icon: '🎶', color: '#FF0000' }
];

// ============================================
// PHASE & RISK CONFIGURATION
// ============================================
export const PHASE_CONFIG = {
  emerging: { emoji: '🚀', color: '#10B981', bgColor: '#D1FAE5', label: 'EMERGING' },
  rising: { emoji: '📈', color: '#3B82F6', bgColor: '#DBEAFE', label: 'RISING' },
  peak: { emoji: '⚠️', color: '#F59E0B', bgColor: '#FEF3C7', label: 'PEAK' },
  declining: { emoji: '📉', color: '#EF4444', bgColor: '#FEE2E2', label: 'DECLINING' },
  stable: { emoji: '➡️', color: '#6B7280', bgColor: '#F3F4F6', label: 'STABLE' },
  fading: { emoji: '❌', color: '#9CA3AF', bgColor: '#F9FAFB', label: 'FADING' }
};

export const RISK_CONFIG = {
  low: { emoji: '🟢', color: '#10B981', label: 'Low Risk' },
  medium: { emoji: '🟡', color: '#F59E0B', label: 'Medium Risk' },
  high: { emoji: '🔴', color: '#EF4444', label: 'High Risk' }
};

// ============================================
// HOT TAGS (Tag List View)
// ============================================
export const DUMMY_HOT_TAGS = {
  region: {
    region_scope: 'global',
    region_code: 'GLOBAL',
    region_name: 'Overall'
  },
  tags: [
    {
      tag_id: 'artificial-intelligence',
      tag_name: 'Artificial Intelligence',
      trending_score: 92.5,
      trending_rank: 1,
      trend_count: 156,
      total_engagement: 4500000,
      platforms: ['youtube', 'reddit', 'tiktok', 'twitter'],
      why_trending_summary: 'Massive interest in AI productivity tools following recent product launches. ChatGPT and Claude updates driving conversation across all platforms.',
      trend_lifecycle: {
        phase: 'rising',
        phase_confidence: 0.92,
        velocity: 3.2,
        acceleration: 0.8,
        estimated_peak_at: '2026-02-01T12:00:00Z',
        time_in_phase: '12h',
        days_active: 5,
        recommendation: 'Trend is hot! Create content within 24-48h for best results.',
        risk_level: 'low'
      },
      updated_at: '2026-01-29T10:00:00Z'
    },
    {
      tag_id: 'content-creation',
      tag_name: 'Content Creation',
      trending_score: 87.3,
      trending_rank: 2,
      trend_count: 124,
      total_engagement: 3200000,
      platforms: ['youtube', 'tiktok', 'instagram'],
      why_trending_summary: 'Creator economy continues to grow. New monetization features on platforms driving interest in content creation strategies.',
      trend_lifecycle: {
        phase: 'peak',
        phase_confidence: 0.85,
        velocity: 1.5,
        acceleration: -0.2,
        estimated_peak_at: null,
        time_in_phase: '24h',
        days_active: 14,
        recommendation: 'At peak! Create immediately or find a unique angle to stand out.',
        risk_level: 'medium'
      },
      updated_at: '2026-01-29T10:00:00Z'
    },
    {
      tag_id: 'productivity',
      tag_name: 'Productivity',
      trending_score: 84.1,
      trending_rank: 3,
      trend_count: 98,
      total_engagement: 2800000,
      platforms: ['youtube', 'reddit'],
      why_trending_summary: 'New year productivity content surge. Focus on AI-enhanced productivity tools and systems.',
      trend_lifecycle: {
        phase: 'stable',
        phase_confidence: 0.78,
        velocity: 0.5,
        acceleration: 0.1,
        estimated_peak_at: null,
        time_in_phase: '7d',
        days_active: 30,
        recommendation: 'Stable trend. Good for evergreen content with unique perspective.',
        risk_level: 'low'
      },
      updated_at: '2026-01-29T10:00:00Z'
    },
    {
      tag_id: 'machine-learning',
      tag_name: 'Machine Learning',
      trending_score: 79.5,
      trending_rank: 4,
      trend_count: 76,
      total_engagement: 2100000,
      platforms: ['youtube', 'reddit', 'twitter'],
      why_trending_summary: 'Growing interest in ML education. Developers looking to upskill with practical ML applications.',
      trend_lifecycle: {
        phase: 'emerging',
        phase_confidence: 0.88,
        velocity: 4.1,
        acceleration: 1.2,
        estimated_peak_at: '2026-02-05T08:00:00Z',
        time_in_phase: '6h',
        days_active: 2,
        recommendation: 'Early opportunity! First-mover advantage available.',
        risk_level: 'low'
      },
      updated_at: '2026-01-29T10:00:00Z'
    },
    {
      tag_id: 'gaming',
      tag_name: 'Gaming',
      trending_score: 76.2,
      trending_rank: 5,
      trend_count: 89,
      total_engagement: 3500000,
      platforms: ['youtube', 'tiktok', 'twitter'],
      why_trending_summary: 'Upcoming game releases generating buzz. GTA 6 anticipation driving massive engagement.',
      trend_lifecycle: {
        phase: 'declining',
        phase_confidence: 0.72,
        velocity: -1.2,
        acceleration: -0.5,
        estimated_peak_at: null,
        time_in_phase: '48h',
        days_active: 21,
        recommendation: 'Past peak. Only proceed with strong unique angle or wait for next wave.',
        risk_level: 'high'
      },
      updated_at: '2026-01-29T10:00:00Z'
    },
    {
      tag_id: 'finance',
      tag_name: 'Personal Finance',
      trending_score: 72.8,
      trending_rank: 6,
      trend_count: 65,
      total_engagement: 1800000,
      platforms: ['youtube', 'reddit', 'tiktok'],
      why_trending_summary: 'New year financial planning content. Focus on budgeting and investment strategies.',
      trend_lifecycle: {
        phase: 'rising',
        phase_confidence: 0.81,
        velocity: 2.1,
        acceleration: 0.4,
        estimated_peak_at: '2026-02-03T16:00:00Z',
        time_in_phase: '18h',
        days_active: 4,
        recommendation: 'Growing trend. Create content within 48-72h for good results.',
        risk_level: 'low'
      },
      updated_at: '2026-01-29T10:00:00Z'
    }
  ],
  pagination: {
    total: 150,
    limit: 50,
    offset: 0
  }
};

// ============================================
// TAG DETAILS
// ============================================
export const DUMMY_TAG_DETAILS = {
  'artificial-intelligence': {
    tag_id: 'artificial-intelligence',
    tag_name: 'Artificial Intelligence',
    region: {
      region_scope: 'global',
      region_code: 'GLOBAL',
      region_name: 'Overall'
    },
    trending_score: 92.5,
    trending_rank: 1,
    metrics: {
      trend_count: 156,
      total_views: 45000000,
      total_likes: 2500000,
      total_comments: 180000,
      platforms: ['youtube', 'reddit', 'tiktok', 'twitter']
    },
    score_components: {
      avg_top_media_score: 82.4,
      frequency_score: 74.2,
      cross_platform_score: 100.0,
      recency_score: 68.5,
      velocity_score: 71.4,
      engagement_score: 64.3
    },
    region_rankings: [
      { region_code: 'GLOBAL', region_name: 'Overall', trending_rank: 1, trending_score: 92.5 },
      { region_code: 'US', region_name: 'United States', trending_rank: 1, trending_score: 94.2 },
      { region_code: 'JP', region_name: 'Japan', trending_rank: 3, trending_score: 88.1 },
      { region_code: 'GB', region_name: 'United Kingdom', trending_rank: 2, trending_score: 90.5 }
    ],
    why_trending_summary: 'Massive interest in AI productivity tools following recent product launches from OpenAI, Anthropic, and Google. ChatGPT-5 and Claude 4 updates are driving conversation across all platforms. Creators are sharing tutorials and comparisons.',
    trend_lifecycle: {
      phase: 'rising',
      phase_confidence: 0.92,
      velocity: 3.2,
      acceleration: 0.8,
      estimated_peak_at: '2026-02-01T12:00:00Z',
      time_in_phase: '12h',
      days_active: 5,
      recommendation: 'Trend is hot! Create content within 24-48h for best results.',
      risk_level: 'low'
    },
    velocity_history: [
      { snapshot_at: '2026-01-29T10:00:00Z', trending_score: 92.5, velocity: 3.2, phase: 'rising' },
      { snapshot_at: '2026-01-29T04:00:00Z', trending_score: 88.1, velocity: 2.8, phase: 'rising' },
      { snapshot_at: '2026-01-28T22:00:00Z', trending_score: 82.5, velocity: 2.5, phase: 'rising' },
      { snapshot_at: '2026-01-28T16:00:00Z', trending_score: 75.2, velocity: 2.1, phase: 'emerging' },
      { snapshot_at: '2026-01-28T10:00:00Z', trending_score: 68.1, velocity: 1.8, phase: 'emerging' },
      { snapshot_at: '2026-01-28T04:00:00Z', trending_score: 62.5, velocity: 1.5, phase: 'emerging' },
      { snapshot_at: '2026-01-27T22:00:00Z', trending_score: 55.2, velocity: 1.2, phase: 'emerging' }
    ],
    content_patterns: {
      recommended_format: {
        format_type: 'video_tutorial',
        reasoning: 'Top-performing content uses step-by-step video tutorials with screen recordings. Average watch time is 8-12 minutes.'
      },
      winning_hooks: [
        {
          hook_type: 'problem-solution',
          example_title: '10 AI Tools That Will Change Your Workflow',
          trend_id: 'youtube_ai001'
        },
        {
          hook_type: 'comparison',
          example_title: 'ChatGPT vs Claude vs Gemini - Ultimate Comparison',
          trend_id: 'youtube_ai002'
        },
        {
          hook_type: 'transformation',
          example_title: 'How AI Changed My Productivity Forever',
          trend_id: 'youtube_ai003'
        }
      ],
    },
    brand_tone: {
      detected_tone: 'educational',
      tone_confidence: 0.72,
      tone_rationale: 'Keywords indicate tutorial framing; platform norms favor instructional tone.',
      tone_sources: ['title', 'description', 'tags', 'platform']
    },
    linked_media: [
      {
        trend_id: 'youtube_ai001',
        title: '10 AI Tools That Will Change Your Workflow in 2026',
        media_url: 'https://youtube.com/watch?v=example1',
        thumbnail_url: 'https://picsum.photos/seed/ai1/320/180',
        platform: 'youtube',
        media_type: 'video',
        engagement: 850000,
        contribution_score: 0.92,
        rank_within_tag: 1,
        published_at: '2026-01-27T14:00:00Z'
      },
      {
        trend_id: 'youtube_ai002',
        title: 'ChatGPT vs Claude vs Gemini - Ultimate Comparison',
        media_url: 'https://youtube.com/watch?v=example2',
        thumbnail_url: 'https://picsum.photos/seed/ai2/320/180',
        platform: 'youtube',
        media_type: 'video',
        engagement: 620000,
        contribution_score: 0.85,
        rank_within_tag: 2,
        published_at: '2026-01-26T10:00:00Z'
      },
      {
        trend_id: 'tiktok_ai001',
        title: 'AI Made Me $10K in One Week',
        media_url: 'https://tiktok.com/@user/video/example',
        thumbnail_url: 'https://picsum.photos/seed/ai3/320/180',
        platform: 'tiktok',
        media_type: 'video',
        engagement: 1200000,
        contribution_score: 0.88,
        rank_within_tag: 3,
        published_at: '2026-01-28T08:00:00Z'
      },
      {
        trend_id: 'reddit_ai001',
        title: 'Complete Guide to AI Tools for Developers',
        media_url: 'https://reddit.com/r/artificial/comments/example',
        thumbnail_url: 'https://picsum.photos/seed/ai4/320/180',
        platform: 'reddit',
        media_type: 'article',
        engagement: 45000,
        contribution_score: 0.72,
        rank_within_tag: 4,
        published_at: '2026-01-25T16:00:00Z'
      }
    ],
    linked_media_by_type: [
      {
        media_type: 'video',
        items: [
          { trend_id: 'youtube_ai001', title: '10 AI Tools That Will Change Your Workflow', media_url: 'https://youtube.com/watch?v=example1', thumbnail_url: 'https://picsum.photos/seed/ai1/320/180', platform: 'youtube', engagement: 850000 },
          { trend_id: 'youtube_ai002', title: 'ChatGPT vs Claude vs Gemini', media_url: 'https://youtube.com/watch?v=example2', thumbnail_url: 'https://picsum.photos/seed/ai2/320/180', platform: 'youtube', engagement: 620000 },
          { trend_id: 'tiktok_ai001', title: 'AI Made Me $10K in One Week', media_url: 'https://tiktok.com/@user/video/example', thumbnail_url: 'https://picsum.photos/seed/ai3/320/180', platform: 'tiktok', engagement: 1200000 }
        ]
      },
      {
        media_type: 'article',
        items: [
          { trend_id: 'reddit_ai001', title: 'Complete Guide to AI Tools', media_url: 'https://reddit.com/r/artificial/example', thumbnail_url: 'https://picsum.photos/seed/ai4/320/180', platform: 'reddit', engagement: 45000 }
        ]
      }
    ],
    history: [
      {
        snapshot_at: '2026-01-29T10:00:00Z',
        trending_score: 92.5,
        trending_rank: 1,
        trend_count: 156,
        why_trending_summary: 'Massive interest in AI productivity tools...'
      },
      {
        snapshot_at: '2026-01-28T10:00:00Z',
        trending_score: 78.2,
        trending_rank: 3,
        trend_count: 98,
        why_trending_summary: 'Growing interest in AI tools following product updates...'
      },
      {
        snapshot_at: '2026-01-27T10:00:00Z',
        trending_score: 65.1,
        trending_rank: 8,
        trend_count: 54,
        why_trending_summary: 'AI tools gaining traction in productivity space...'
      }
    ],
    first_seen_at: '2026-01-24T08:00:00Z',
    updated_at: '2026-01-29T10:00:00Z'
  }
};

// ============================================
// LATEST MEDIA
// ============================================
export const DUMMY_LATEST_MEDIA = {
  region: {
    region_scope: 'global',
    region_code: 'GLOBAL',
    region_name: 'Overall'
  },
  selected_platform: null,
  filters: {
    platform: 'all',
    media_type: 'all'
  },
  items: [
    {
      trend_id: 'youtube_ai001',
      platform: 'youtube',
      media_type: 'video',
      title: '10 AI Tools That Will Change Your Workflow in 2026',
      description: 'Discover the top AI tools that are revolutionizing how we work. From writing to coding to design, these tools will boost your productivity.',
      media_url: 'https://youtube.com/watch?v=example1',
      thumbnail_url: 'https://picsum.photos/seed/ai1/320/180',
      source_tags: [
        { tag: 'ai', source: 'hashtag' },
        { tag: 'productivity', source: 'hashtag' },
        { tag: 'Science & Technology', source: 'category' }
      ],
      metrics: {
        views: 850000,
        likes: 45000,
        comments: 2800
      },
      published_at: '2026-01-27T14:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'youtube_ai002',
      platform: 'youtube',
      media_type: 'video',
      title: 'ChatGPT vs Claude vs Gemini - Ultimate Comparison 2026',
      description: 'Which AI assistant is the best? We put all three to the test with real-world tasks.',
      media_url: 'https://youtube.com/watch?v=example2',
      thumbnail_url: 'https://picsum.photos/seed/ai2/320/180',
      source_tags: [
        { tag: 'chatgpt', source: 'hashtag' },
        { tag: 'ai', source: 'hashtag' }
      ],
      metrics: {
        views: 620000,
        likes: 38000,
        comments: 3200
      },
      published_at: '2026-01-26T10:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'tiktok_ai001',
      platform: 'tiktok',
      media_type: 'video',
      title: 'AI Made Me $10K in One Week 🤯',
      description: 'How I used AI tools to generate passive income',
      media_url: 'https://tiktok.com/@user/video/example',
      thumbnail_url: 'https://picsum.photos/seed/ai3/320/180',
      source_tags: [
        { tag: 'ai', source: 'hashtag' },
        { tag: 'money', source: 'hashtag' },
        { tag: 'sidehustle', source: 'hashtag' }
      ],
      metrics: {
        views: 1200000,
        likes: 180000,
        comments: 4500,
        shares: 25000
      },
      published_at: '2026-01-28T08:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'reddit_ai001',
      platform: 'reddit',
      media_type: 'article',
      title: 'Complete Guide to AI Tools for Developers in 2026',
      description: 'A comprehensive list of AI tools every developer should know about',
      media_url: 'https://reddit.com/r/artificial/comments/example',
      thumbnail_url: 'https://picsum.photos/seed/ai4/320/180',
      source_tags: [
        { tag: 'artificial', source: 'subreddit' }
      ],
      metrics: {
        upvotes: 4500,
        comments: 320
      },
      published_at: '2026-01-25T16:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'youtube_cc001',
      platform: 'youtube',
      media_type: 'video',
      title: 'How I Grew to 1M Subscribers in 6 Months',
      description: 'My complete YouTube growth strategy revealed',
      media_url: 'https://youtube.com/watch?v=cc1',
      thumbnail_url: 'https://picsum.photos/seed/cc1/320/180',
      source_tags: [
        { tag: 'youtube', source: 'hashtag' },
        { tag: 'growth', source: 'hashtag' }
      ],
      metrics: {
        views: 2100000,
        likes: 125000,
        comments: 8500
      },
      published_at: '2026-01-26T12:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'tiktok_cc001',
      platform: 'tiktok',
      media_type: 'video',
      title: 'Content Creation Tips for Beginners ✨',
      description: '5 tips that helped me go viral',
      media_url: 'https://tiktok.com/@creator/video/cc1',
      thumbnail_url: 'https://picsum.photos/seed/cc2/320/180',
      source_tags: [
        { tag: 'contentcreator', source: 'hashtag' },
        { tag: 'tips', source: 'hashtag' }
      ],
      metrics: {
        views: 3500000,
        likes: 450000,
        comments: 12000,
        shares: 65000
      },
      published_at: '2026-01-28T15:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'youtube_music001',
      platform: 'youtube_music',
      media_type: 'audio',
      title: 'Summer Vibes 2026 - Top Hits Playlist',
      description: 'The hottest tracks of 2026',
      media_url: 'https://music.youtube.com/playlist/example',
      thumbnail_url: 'https://picsum.photos/seed/music1/320/180',
      source_tags: [
        { tag: 'music', source: 'category' }
      ],
      metrics: {
        views: 5600000,
        likes: 89000
      },
      published_at: '2026-01-20T08:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    },
    {
      trend_id: 'instagram_cc001',
      platform: 'instagram',
      media_type: 'image',
      title: 'My Content Setup Tour 2026',
      description: 'Finally showing my home studio setup!',
      media_url: 'https://instagram.com/p/example',
      thumbnail_url: 'https://picsum.photos/seed/cc3/320/180',
      source_tags: [
        { tag: 'setup', source: 'hashtag' },
        { tag: 'creator', source: 'hashtag' }
      ],
      metrics: {
        likes: 125000,
        comments: 3400,
        saves: 18000
      },
      published_at: '2026-01-27T09:00:00Z',
      collected_at: '2026-01-29T08:00:00Z'
    }
  ],
  pagination: {
    total: 200,
    limit: 50,
    offset: 0
  }
};

// ============================================
// MEDIA DETAILS
// ============================================
export const DUMMY_MEDIA_DETAILS = {
  'youtube_ai001': {
    trend_id: 'youtube_ai001',
    platform: 'youtube',
    media_type: 'video',
    title: '10 AI Tools That Will Change Your Workflow in 2026',
    description: 'Discover the top AI tools that are revolutionizing how we work. In this video, I share my favorite tools for writing, coding, design, and more. These tools have saved me hours every week and boosted my productivity significantly.',
    media_url: 'https://youtube.com/watch?v=example1',
    thumbnail_url: 'https://picsum.photos/seed/ai1/640/360',
    region: {
      region_scope: 'global',
      region_code: 'GLOBAL',
      region_name: 'Overall'
    },
    region_ranks: [
      { region_code: 'GLOBAL', region_name: 'Overall', rank: 1 },
      { region_code: 'US', region_name: 'United States', rank: 1 },
      { region_code: 'GB', region_name: 'United Kingdom', rank: 2 }
    ],
    source_tags: [
      { tag: 'ai', source: 'hashtag' },
      { tag: 'productivity', source: 'hashtag' },
      { tag: 'Science & Technology', source: 'category' }
    ],
    referenced_trending_tags: ['artificial-intelligence', 'productivity', 'content-creation'],
    metrics: {
      views: 850000,
      likes: 45000,
      comments: 2800,
      shares: 12000
    },
    analytics: {
      usp: 'Practical demonstration with real workflow examples. Shows actual productivity gains with specific numbers.',
      structure: 'Hook → Problem → Tool Demo → Results → CTA',
      performance: 'High engagement rate (5.3%). Strong first 48h performance with 60% of views coming in first 2 days.'
    },
    style_signals: {
      format: 'tutorial',
      visual_style: 'screen_recording_with_facecam',
      pacing: 'medium',
      editing: 'professional'
    },
    learn_from_this: {
      why_this_works: 'This video succeeds because it combines practical value with engaging presentation. The creator shows real examples from their workflow, which builds trust and makes the content actionable.',
      hook_analysis: {
        hook_type: 'problem-solution',
        strength: 92,
        first_seconds_text: 'Tired of wasting hours on repetitive tasks? In this video, I\'ll show you 10 AI tools that have saved me 15+ hours every week.',
        why_effective: 'Immediately addresses a common pain point (wasted time) and promises a specific, measurable benefit (15+ hours saved).'
      },
      structure_breakdown: {
        sections: [
          { time_range: '0:00-0:30', section: 'Hook', content: 'Problem statement + promise' },
          { time_range: '0:30-2:00', section: 'Context', content: 'Why AI tools matter now' },
          { time_range: '2:00-10:00', section: 'Main Content', content: 'Demo of 10 tools with examples' },
          { time_range: '10:00-11:00', section: 'Recap + CTA', content: 'Summary and call to action' }
        ],
        pacing: 'Medium - allows time for comprehension',
        transitions: 'Smooth cuts with motion graphics'
      },
      what_to_borrow: [
        'Start with a specific number in the title (10 tools)',
        'Show real examples from your own workflow',
        'Include before/after productivity metrics',
        'Use screen recordings with facecam for engagement',
        'End with a clear, single call to action'
      ],
      how_to_differentiate: [
        'Focus on a specific niche (e.g., AI tools for designers only)',
        'Include pricing comparisons and ROI analysis',
        'Add user interviews or testimonials',
        'Create a follow-up series going deep on each tool',
        'Test tools against each other with the same task'
      ]
    },
    published_at: '2026-01-27T14:00:00Z',
    collected_at: '2026-01-29T08:00:00Z',
    updated_at: '2026-01-29T08:00:00Z'
  }
};

const dummyData = {
  DUMMY_REGIONS,
  DUMMY_PLATFORMS,
  PHASE_CONFIG,
  RISK_CONFIG,
  DUMMY_HOT_TAGS,
  DUMMY_TAG_DETAILS,
  DUMMY_LATEST_MEDIA,
  DUMMY_MEDIA_DETAILS
};

export default dummyData;
